// Venue-aware balanced-bin-pack planner.
//
// Inputs: services (already partitioned by spatial group via touring_identity),
//   the target venue's house-feed inventory, and a utilization target
//   (Safest 70% / Balanced 80% / Aggressive 90%).
//
// Algorithm: for each spatial partition, rank the target venue's feeds by
// proximity (exact location-label match first, then the built-in adjacency
// table, then any remaining feed). Greedy place services onto feeds — prefer
// adding to existing partially-loaded feeds when the operator-set utilization
// target still permits, else open a new feed at the next proximity tier. Sean:
// "balanced bin packing from nearest house feeds" + "don't prioritize smallest
// feeds — extra overhead isn't a problem."
//
// Pure function — no side effects, no fetches. The Suggestions view fetches
// venue feeds + services_input then calls this for each strategy.

import type { SuggestionsServiceInput } from "@/lib/types";
import type { VenueHouseFeed } from "@/lib/api";

export type ProximityTier = "assigned" | "exact" | "adjacent" | "fallback";

export interface FeedAssignment {
  feed: VenueHouseFeed;
  services: SuggestionsServiceInput[];
  /** Sum of each service's sizing amperage (per-strategy: usually
   *  worst_coincident_peak_a). */
  sum_amps: number;
  /** sum_amps / feed.capacity_amps, as a ratio in [0, 1+]. >1 means
   *  the placer was forced to overpack (no eligible feed had headroom). */
  utilization: number;
  /** Which proximity tier did this feed come from for the partition? */
  proximity: ProximityTier;
  /** Numeric affinity score that drove the placer. Higher = closer match.
   *  Within the same tier, candidates with higher affinity are picked
   *  first. Exposed so the UI can rank assignments + optionally surface
   *  the reason. 0 for fallback. */
  affinity: number;
  /** Sum-of-show_condition_max across EVERY partition sharing this feed
   *  (not just this partition's services). When `cross_partition_services`
   *  is non-empty, `feed_sum_amps_total > sum_amps`. Drives the utilization
   *  gauge so the operator sees the true combined load. */
  feed_sum_amps_total: number;
  /** Services from OTHER partitions also placed on this feed (empty when
   *  the feed is not shared). Each carries the partition's touring
   *  identity for context. */
  cross_partition_services: Array<{
    touring_identity: string | null;
    services: SuggestionsServiceInput[];
  }>;
}

export interface PartitionPlan {
  tier: string | null;
  touring_identity: string | null;
  assignments: FeedAssignment[];
  /** Services we couldn't place anywhere even at fallback tier (the venue
   *  has no feeds with any remaining headroom for this load). */
  unplaced: SuggestionsServiceInput[];
}

export interface VenuePlan {
  partitions: PartitionPlan[];
  /** Feeds the planner did NOT use at all (operator can release them). */
  unused_feeds: VenueHouseFeed[];
  /** Set when the caller requested strictNoFallback but the venue has
   *  zero declared feeds (served_identities empty everywhere) — strict
   *  would have unplaced every service. The planner auto-degrades to
   *  permissive so the operator still sees a useful first-pass plan;
   *  the UI surfaces a banner pointing them at /admin/venues to declare
   *  identities. */
  strict_auto_disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Proximity model — four tiers, evaluated top-down:
//
//   1. ASSIGNED — the operator has explicitly declared at /admin/venues
//      that this feed serves the partition's touring identity. Strongest
//      signal: operator intent overrides every heuristic. Reads
//      feed.served_identities (canonicalized) and checks whether the
//      partition's touring_identity (also canonicalized) appears.
//   2. EXACT — literal equality of the partition's location label and the
//      feed's location label.
//   3. ADJACENT — derived match. Either:
//        (a) shared content token (after dropping pure-directional words
//            like "left"/"right"), or
//        (b) global hardcoded ADJACENCY map (foh↔house center, etc.).
//      Within "adjacent", an affinity score ranks closer matches above
//      weaker ones so "stage left" beats "stage right" when filling an
//      "upstage left" partition.
//   4. FALLBACK — no relationship found.
//
// Synonym groups handle single-token operational equivalents (foh↔house,
// mons↔monitors) so the assigned-tier check sees one canonical entry per
// concept and the token-overlap check matches across vocabularies.
// ---------------------------------------------------------------------------

// Directional modifiers — these tokens describe *position* but not *kind of
// place*. A label sharing only a directional with another label (e.g.
// "upstage left" + "house left" both have "left") is NOT a meaningful match
// — they're on opposite sides of the venue. Filter directionals out before
// content-token matching.
const DIRECTIONALS = new Set([
  "left", "right", "center", "east", "west", "north", "south",
  "upper", "lower", "mid", "middle", "central", "top", "bottom",
]);

// Operational-vocabulary synonyms — tokens that should content-match each
// other because they refer to the same kind of place but use different
// touring/venue jargon. First-class synonyms only; the table is
// deliberately small and not ever-expanding so the vocabulary stays
// predictable. Keep in sync with _IDENTITY_SYNONYMS in
// cloud/services/api/routes/venues.py.
//
// Stored as a CANONICAL FORM map: each member of a synonym group maps to
// the same canonical string. Used both for token-level content matching
// (adjacent tier) and whole-label canonicalization (assigned tier).
const IDENTITY_SYNONYMS: Record<string, string> = {
  // Front-of-house — touring tech says "FOH", venue labels say "house".
  foh: "foh",
  house: "foh",
  // Monitor world.
  mons: "monitors",
  monitor: "monitors",
  monitors: "monitors",
  // Back-of-house / loading.
  back: "boh",
  boh: "boh",
};

function inSameSynonymGroup(a: string, b: string): boolean {
  const ca = IDENTITY_SYNONYMS[a];
  if (ca == null) return false;
  return ca === IDENTITY_SYNONYMS[b];
}

/** Canonicalize a whole-label identity for the 'assigned' tier check.
 *  Single-token labels fold through IDENTITY_SYNONYMS. Multi-token labels
 *  ("upstage right", "house left") pass through normalized — we do NOT
 *  rewrite "house left" → "foh left" because that would produce labels
 *  nobody ever writes. Mirrors _canonicalize_identity in venues.py. */
function canonicalizeIdentity(raw: string | null | undefined): string {
  const n = (raw ?? "").trim().toLowerCase();
  if (!n) return "";
  if (n.includes(" ") || n.includes("/")) return n;
  return IDENTITY_SYNONYMS[n] ?? n;
}

function tokenize(label: string): string[] {
  return label
    .split(/[\s/]+/)
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.length >= 2);
}

function contentTokensOf(label: string): string[] {
  return tokenize(label).filter((t) => !DIRECTIONALS.has(t));
}

function contentTokensMatch(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.length >= 3 && b.includes(a)) return true;
  if (b.length >= 3 && a.includes(b)) return true;
  // Synonym fold — "house" matches "foh" even though they share no chars.
  if (inSameSynonymGroup(a, b)) return true;
  return false;
}

function countContentMatches(labelA: string, labelB: string): number {
  // Count distinct content tokens of A that find a match in B. Symmetric in
  // practice — the count is dominated by the smaller token set. Counts each
  // A token at most once, so "stage left wing" vs "stage" still yields 1.
  const ca = contentTokensOf(labelA);
  const cb = contentTokensOf(labelB);
  let matched = 0;
  for (const a of ca) {
    if (cb.some((b) => contentTokensMatch(a, b))) matched += 1;
  }
  return matched;
}

function shareDirectional(labelA: string, labelB: string): boolean {
  const dA = tokenize(labelA).filter((t) => DIRECTIONALS.has(t));
  if (dA.length === 0) return false;
  const dB = new Set(tokenize(labelB).filter((t) => DIRECTIONALS.has(t)));
  for (const t of dA) if (dB.has(t)) return true;
  return false;
}

const ADJACENCY: Record<string, string[]> = {
  "upstage left": ["grid/catwalk", "upstage center"],
  "upstage right": ["grid/catwalk", "upstage center"],
  "upstage center": ["upstage left", "upstage right", "grid/catwalk"],
  "grid/catwalk": ["upstage left", "upstage right", "upstage center"],
  "house center": ["foh", "mid court east", "catering"],
  "house left": ["foh", "mid court east"],
  "house right": ["foh", "mid court east"],
  "foh": ["house center", "house left", "house right", "mid court east"],
  "mid court east": ["foh", "house center"],
  "catering": ["foh"],
};

function normLabel(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

function inMap(
  a: string,
  b: string,
  map: Record<string, string[]> | undefined,
): boolean {
  if (!map) return false;
  return (map[a] ?? []).includes(b) || (map[b] ?? []).includes(a);
}

export interface ProximityResult {
  /** 4-tier bucket used for the planner's pass-ordering + UI badge. */
  tier: ProximityTier;
  /** Higher = better match within the same tier. Used as the primary sort
   *  key when picking a feed for a partition so a closer adjacent match
   *  (e.g. content-cluster + matching directional) beats a weaker one
   *  (content-cluster only, or merely in a configured adjacency map).
   *  0 for fallback. */
  affinity: number;
}

function proximityFor(
  partitionLocation: string | null,
  partitionIdentity: string | null,
  feedLocation: string | null,
  feedServedIdentities: string[] | undefined,
): ProximityResult {
  // 1. ASSIGNED — operator-declared. Strongest signal. If the partition has
  //    a touring_identity and the feed lists it (after canonicalization),
  //    short-circuit at the top tier regardless of label labels.
  const ident = canonicalizeIdentity(partitionIdentity);
  if (ident && feedServedIdentities && feedServedIdentities.length > 0) {
    for (const raw of feedServedIdentities) {
      if (canonicalizeIdentity(raw) === ident) {
        return { tier: "assigned", affinity: 20 };
      }
    }
  }

  // The remaining tiers compare label-to-label using the partition's
  // location label (which falls back to touring_identity upstream).
  const svc = normLabel(partitionLocation);
  const fd = normLabel(feedLocation);
  if (!svc || !fd) return { tier: "fallback", affinity: 0 };
  if (svc === fd) return { tier: "exact", affinity: 10 };

  // Compute the strongest signal from each available source. Content match
  // dominates; configured maps are a smaller bonus. The score is what lets
  // "stage left" beat "stage right" when both share the same content
  // cluster with an "upstage left" partition.
  let affinity = 0;
  const contentMatches = countContentMatches(svc, fd);
  if (contentMatches > 0) {
    // Base 3 for any content match, +1 per additional content token shared
    // (a multi-token alignment is stronger than a single token), +1 if the
    // labels also share a directional (left/right/etc — same wing of the
    // venue, not the opposite side).
    affinity = 3 + (contentMatches - 1) + (shareDirectional(svc, fd) ? 1 : 0);
  } else if (inMap(svc, fd, ADJACENCY)) {
    // Global hardcoded fallback — catches common acronym/synonym pairs
    // (foh ↔ house center/left/right, upstage ↔ grid). Per-venue
    // overrides land at the 'assigned' tier above; this map is just for
    // un-configured venues with vocabulary token overlap can't bridge.
    affinity = 1;
  }

  // Service-feed fallback boost — when nothing above matched, prefer a
  // feed the operator has DECLARED (any served_identities) over a feed
  // with empty served_identities. At Kia Forum: an FOH-side overflow
  // ranks Stage Left (declared "stage left") above Catwalk (empty —
  // reserved for rigging). This is the "share with stage feeds before
  // landing on catwalk" rule. Still fallback tier, just ranks above 0.
  if (affinity === 0 && feedServedIdentities && feedServedIdentities.length > 0) {
    affinity = 0.5;
  }

  // Tier cut: only ≥1 promotes to 'adjacent'. 0.5 stays at fallback but
  // beats truly-undeclared (0) on the affinity sort.
  return { tier: affinity >= 1 ? "adjacent" : "fallback", affinity };
}

// ---------------------------------------------------------------------------
// Per-partition placement — greedy balanced bin-pack.
// ---------------------------------------------------------------------------

/** Pick the partition's preferred location label. All services in a partition
 *  share their `service_location_label` (operator-tagged at load-in); we use
 *  the first non-null label as the preference proxy. Falls back to the
 *  touring_identity. */
function partitionLocationLabel(
  services: SuggestionsServiceInput[],
): string | null {
  for (const s of services) {
    if (s.service_location_label) return s.service_location_label;
  }
  for (const s of services) {
    if (s.touring_identity) return s.touring_identity;
  }
  return null;
}


// ---------------------------------------------------------------------------
// Top-level planner.
// ---------------------------------------------------------------------------

export interface PlanVenueArgs {
  services: SuggestionsServiceInput[];
  feeds: VenueHouseFeed[];
  /** Per-strategy: Safest 0.70, Balanced 0.80, Aggressive 0.90. */
  utilizationTarget: number;
  /** Per-strategy sizing: Safest + Balanced use show_condition_max_a
   *  (telemetry-derived expected show peak). */
  sizingField?: "worst_coincident_peak_a" | "show_condition_max_a";
  /** Aggressive packs more services per feed knowing coincidence math
   *  (computed after placement via Tier 1 overlay) will typically reduce
   *  the real peak by 25-40%. Multiplier > 1 tells the placer to pack up
   *  to `capacity × utilization × diversityMultiplier` worth of sum.
   *  Safest/Balanced should leave at 1.0; Aggressive ~1.3. */
  diversityMultiplier?: number;
  /** When true, the planner refuses to place services on undeclared
   *  feeds (`served_identities = []`, e.g. Catwalks reserved for
   *  rigging, Broadcast for TV crews). Services that don't fit on any
   *  declared feed land in `unplaced` so the operator sees a red warning
   *  instead of a quiet "far" tag. Safest + Balanced default to true;
   *  Aggressive to false. */
  strictNoFallback?: boolean;
}

export function planVenue({
  services,
  feeds,
  utilizationTarget,
  sizingField = "show_condition_max_a",
  diversityMultiplier = 1.0,
  strictNoFallback = false,
}: PlanVenueArgs): VenuePlan {
  const activeFeeds = feeds.filter((f) => f.deleted_at == null);
  const placementBudget = utilizationTarget * diversityMultiplier;
  // Auto-degrade strict mode when zero feeds at the venue are declared
  // (served_identities all empty). Strict + no declarations would unplace
  // every service — that's not actionable feedback, it's just punishing
  // the operator before they've done declaration work. Give them a
  // permissive first-pass plan + a banner pointing at /admin/venues to
  // declare identities for a tighter plan.
  const anyDeclared = activeFeeds.some(
    (f) => (f.served_identities ?? []).length > 0,
  );
  const effectiveStrict = strictNoFallback && anyDeclared;
  const strict_auto_disabled = strictNoFallback && !anyDeclared;

  // Build partition metadata up-front (partition key + preferred location
  // for proximity ranking). Untagged services land in their own partition.
  const partitionInfo = new Map<string, {
    services: SuggestionsServiceInput[];
    locationLabel: string | null;
    tier: string | null;
    touring_identity: string | null;
  }>();
  for (const svc of services) {
    const key = `${svc.tier ?? "none"}|${svc.touring_identity ?? "untagged"}`;
    let bucket = partitionInfo.get(key);
    if (!bucket) {
      bucket = {
        services: [],
        locationLabel: null,
        tier: svc.tier ?? null,
        touring_identity: svc.touring_identity ?? null,
      };
      partitionInfo.set(key, bucket);
    }
    bucket.services.push(svc);
  }
  for (const info of partitionInfo.values()) {
    info.locationLabel = partitionLocationLabel(info.services);
  }

  // Flat global service queue, sorted by sizing DESCENDING. The largest
  // single service (which has the least flexibility — fewer feeds can
  // accommodate it) places first. This is first-fit-decreasing bin-pack
  // applied globally rather than per-partition, so a small service in a
  // hot partition doesn't grab a feed that a larger service from another
  // partition genuinely needs.
  type FlatItem = {
    svc: SuggestionsServiceInput;
    sizing: number;
    partitionKey: string;
  };
  const queue: FlatItem[] = [];
  for (const [pk, info] of partitionInfo) {
    for (const s of info.services) {
      const sz = (s[sizingField] as number) || 0;
      if (sz > 0) queue.push({ svc: s, sizing: sz, partitionKey: pk });
    }
  }
  queue.sort((a, b) => b.sizing - a.sizing);

  // Per-partition assignments accumulated as we go. Each assignment knows
  // its feed + the services placed on that feed.
  type Assignment = {
    feed: VenueHouseFeed;
    services: SuggestionsServiceInput[];
    sum_amps: number;
    proximity: ProximityTier;
    affinity: number;
  };
  const partitionAssignments = new Map<string, Map<string, Assignment>>();
  const partitionUnplaced = new Map<string, SuggestionsServiceInput[]>();
  for (const pk of partitionInfo.keys()) {
    partitionAssignments.set(pk, new Map());
    partitionUnplaced.set(pk, []);
  }

  // Feed exclusivity across partitions: once partition P opens feed F,
  // other partitions cannot also use F (would double-book the breaker)
  // UNLESS the feed is operator-declared as a service feed (any non-empty
  // served_identities). The declaration is the explicit "share this"
  // signal — e.g. a Stage Left feed declared for ["stage left"] can host
  // both an SL Floor partition AND a FOH overflow service. Catwalks /
  // docks / unassigned feeds stay exclusive (reserved for rigging,
  // loading, etc.).
  const feedOwner = new Map<string, string>(); // feed_id → first-touching partition
  const feedPartitions = new Map<string, Set<string>>(); // feed_id → all partitions on this feed
  // Per-feed accumulated sum (across ALL partitions sharing it). Drives
  // both the capacity headroom check and the displayed utilization. For
  // Safest/Balanced this is sum-of-show_condition_max — additive worst
  // case, NO coincidence reduction (the operator's "share with stage
  // feeds before catwalks" rule keeps Safest honest about combined load).
  // For Aggressive the Tier 1 overlay refines the displayed peak via
  // telemetry, but this sum is still the placement-budget gate.
  const globalFeedSums = new Map<string, number>();

  function bumpFeedSum(feedId: string, partitionKey: string, amps: number) {
    globalFeedSums.set(feedId, (globalFeedSums.get(feedId) ?? 0) + amps);
    let parts = feedPartitions.get(feedId);
    if (!parts) {
      parts = new Set();
      feedPartitions.set(feedId, parts);
    }
    parts.add(partitionKey);
    if (!feedOwner.has(feedId)) feedOwner.set(feedId, partitionKey);
  }

  for (const item of queue) {
    const partitionInfoEntry = partitionInfo.get(item.partitionKey)!;
    const myAssignments = partitionAssignments.get(item.partitionKey)!;
    const partitionLoc = partitionInfoEntry.locationLabel;
    const partitionIdentity = partitionInfoEntry.touring_identity;

    // Candidate feeds = (already-open for THIS partition) ∪
    //                   (closed feeds owned by no other partition) ∪
    //                   (feeds owned by another partition IF the feed
    //                    has any served_identities = operator-declared
    //                    service feed = cross-partition share allowed).
    type Cand = {
      feed: VenueHouseFeed;
      prox: ProximityTier;
      affinity: number;
      alreadyOpen: boolean;
      remainingHeadroom: number;
    };
    const candidates: Cand[] = [];
    for (const f of activeFeeds) {
      const owner = feedOwner.get(f.feed_id);
      const isMine = owner === item.partitionKey;
      const isDeclaredServiceFeed = (f.served_identities ?? []).length > 0;
      // Cross-partition exclusivity bypass: declared service feeds (any
      // served_identities set by the operator) are share-OK by default.
      // Undeclared feeds (catwalks, docks) stay exclusive.
      if (owner != null && !isMine && !isDeclaredServiceFeed) continue;
      const existing = myAssignments.get(f.feed_id);
      // Headroom uses the GLOBAL feed sum across all partitions sharing
      // this feed, not just this partition's contribution. Keeps Safest
      // / Balanced honest about the combined load even though the
      // FeedAssignment is per-partition. globalFeedSums accumulates
      // sum-of-show_condition_max for Safest/Balanced (no coincidence
      // reduction); Aggressive overlays Tier 1 telemetry-driven
      // coincidence_peak_a separately on the displayed value.
      const remainingHeadroom =
        f.capacity_amps * placementBudget - (globalFeedSums.get(f.feed_id) ?? 0);
      const px = proximityFor(
        partitionLoc,
        partitionIdentity,
        f.location_label,
        f.served_identities,
      );
      candidates.push({
        feed: f,
        prox: px.tier,
        affinity: px.affinity,
        alreadyOpen: existing != null,
        remainingHeadroom,
      });
    }

    // Selection rules (per Sean's clarified preference):
    //   "Always use the largest feeds available. Only combine services
    //    or use smaller feeds as a last resort for proximity."
    //
    // Algorithm: walk proximity tiers in order (exact → adjacent →
    // fallback). At each tier:
    //   1. Prefer EMPTY feeds (one service per feed), largest capacity
    //      first. This is the default — operator gets a dedicated
    //      breaker per service when inventory allows.
    //   2. Only when no empty feed at this tier fits, look at
    //      already-open feeds for this partition (consolidation).
    //   3. Only when neither path works at this tier do we drop to
    //      the next proximity tier.
    //
    // Combining services (step 2) is the operator's "last resort for
    // proximity" — it happens only when the venue has run out of empty
    // feeds at the right location AND we'd otherwise have to leave
    // proximity to find one.
    // Consolidation-first policy (Sean's clarified preference, Kia Forum
    // tuning round): within a tier, prefer ALREADY-OPEN feeds for this
    // partition before opening a new empty one. Packing services onto
    // shared feeds within a partition leaves more empty declared feeds
    // available for OTHER partitions to share — which is the whole
    // point of the per-feed served_identities model. Largest-feed bias
    // still kicks in when we DO open an empty one.
    const proxOrder: ProximityTier[] = ["assigned", "exact", "adjacent", "fallback"];
    let pick: Cand | undefined;
    for (const tier of proxOrder) {
      const atTier = candidates.filter((c) => c.prox === tier);
      if (atTier.length === 0) continue;
      // 1. Try consolidating onto an existing same-partition feed at this
      //    tier. Tightest fit first (smallest headroom that still fits)
      //    so we don't leave a tiny shard of headroom unused.
      const opens = atTier
        .filter((c) => c.alreadyOpen && c.remainingHeadroom >= item.sizing)
        .sort((a, b) => a.remainingHeadroom - b.remainingHeadroom);
      if (opens.length > 0) { pick = opens[0]; break; }
      // 2. Open a new empty feed. Largest capacity first, breaking ties
      //    by affinity then feed_name for stability.
      const empties = atTier
        .filter((c) => !c.alreadyOpen && c.remainingHeadroom >= item.sizing)
        .sort((a, b) => {
          if (a.affinity !== b.affinity) return b.affinity - a.affinity;
          if (a.feed.capacity_amps !== b.feed.capacity_amps) {
            return b.feed.capacity_amps - a.feed.capacity_amps;
          }
          return a.feed.feed_name.localeCompare(b.feed.feed_name);
        });
      if (empties.length > 0) { pick = empties[0]; break; }
    }
    if (!pick) {
      // Nothing fit anywhere within budget. Force-fit onto the largest
      // available feed at the closest proximity; operator sees the
      // over-target % in red.
      const po: Record<ProximityTier, number> =
        { assigned: 0, exact: 1, adjacent: 2, fallback: 3 };
      const forceable = [...candidates].sort((a, b) => {
        if (po[a.prox] !== po[b.prox]) return po[a.prox] - po[b.prox];
        if (a.affinity !== b.affinity) return b.affinity - a.affinity;
        // Prefer an already-open feed (smaller incremental over-target).
        if (a.alreadyOpen !== b.alreadyOpen) {
          return a.alreadyOpen ? -1 : 1;
        }
        return b.feed.capacity_amps - a.feed.capacity_amps;
      });
      pick = forceable[0];
      if (!pick) {
        partitionUnplaced.get(item.partitionKey)!.push(item.svc);
        continue;
      }
    }
    let assignment = myAssignments.get(pick.feed.feed_id);
    if (!assignment) {
      assignment = {
        feed: pick.feed,
        services: [],
        sum_amps: 0,
        proximity: pick.prox,
        affinity: pick.affinity,
      };
      myAssignments.set(pick.feed.feed_id, assignment);
    }
    assignment.services.push(item.svc);
    assignment.sum_amps += item.sizing;
    // Global accounting — captures cross-partition contributions so the
    // next iteration's headroom math sees the true combined load.
    bumpFeedSum(pick.feed.feed_id, item.partitionKey, item.sizing);
  }

  // -------------------------------------------------------------------------
  // POST-PASS RELOCATOR (depth-1) — try to move undeclared placements onto
  // declared feeds that have room. Greedy Pass 1 above prefers high-affinity
  // candidates so undeclared placements should be rare, but they happen when:
  //   - Pass 1 picked an undeclared feed in a tier all-equal scenario, OR
  //   - A later service consolidated onto a same-partition open feed,
  //     freeing capacity on the original declared feed that an earlier
  //     undeclared placement could have used.
  // The relocator runs until no further improvement; depth-1 only (no
  // recursive sacrifices) so the algorithm is bounded and easy to reason
  // about. Each move strictly improves the placement (declared feed >
  // undeclared at affinity 0).
  // -------------------------------------------------------------------------
  function feedIsDeclared(f: VenueHouseFeed): boolean {
    return (f.served_identities ?? []).length > 0;
  }
  function removeServiceFromState(
    feedId: string, partitionKey: string, sizing: number,
  ) {
    const cur = globalFeedSums.get(feedId) ?? 0;
    globalFeedSums.set(feedId, Math.max(0, cur - sizing));
    const partAssn = partitionAssignments.get(partitionKey)?.get(feedId);
    if (partAssn && partAssn.services.length === 0) {
      partitionAssignments.get(partitionKey)?.delete(feedId);
      const parts = feedPartitions.get(feedId);
      parts?.delete(partitionKey);
      if (feedOwner.get(feedId) === partitionKey) {
        const remaining = parts ? [...parts] : [];
        if (remaining.length > 0) feedOwner.set(feedId, remaining[0]);
        else feedOwner.delete(feedId);
      }
    }
  }

  let relocated = true;
  let relocIter = 0;
  while (relocated && relocIter < 20) {
    relocated = false;
    relocIter += 1;
    outer: for (const [pk, assignmentsMap] of partitionAssignments) {
      const info = partitionInfo.get(pk)!;
      for (const assignment of [...assignmentsMap.values()]) {
        if (feedIsDeclared(assignment.feed)) continue;
        for (const svc of [...assignment.services]) {
          const sizing = (svc[sizingField] as number) || 0;
          if (sizing <= 0) continue;
          // Best declared target across the venue (any partition's feeds
          // — declared feeds are share-OK via the cross-partition bypass).
          let bestFeed: VenueHouseFeed | null = null;
          let bestPx: ProximityResult | null = null;
          for (const f of activeFeeds) {
            if (!feedIsDeclared(f)) continue;
            const used = globalFeedSums.get(f.feed_id) ?? 0;
            const room = f.capacity_amps * placementBudget - used;
            if (room < sizing) continue;
            const px = proximityFor(
              info.locationLabel,
              info.touring_identity,
              f.location_label,
              f.served_identities,
            );
            if (px.affinity <= 0) continue;
            if (!bestFeed || px.affinity > (bestPx?.affinity ?? 0)) {
              bestFeed = f;
              bestPx = px;
            }
          }
          if (bestFeed && bestPx) {
            // Remove from current (undeclared) feed.
            assignment.services = assignment.services.filter((s) => s !== svc);
            assignment.sum_amps -= sizing;
            removeServiceFromState(assignment.feed.feed_id, pk, sizing);
            // Add to declared target.
            let dst = partitionAssignments.get(pk)!.get(bestFeed.feed_id);
            if (!dst) {
              dst = {
                feed: bestFeed,
                services: [],
                sum_amps: 0,
                proximity: bestPx.tier,
                affinity: bestPx.affinity,
              };
              partitionAssignments.get(pk)!.set(bestFeed.feed_id, dst);
            }
            dst.services.push(svc);
            dst.sum_amps += sizing;
            bumpFeedSum(bestFeed.feed_id, pk, sizing);
            relocated = true;
            break outer;
          }
        }
      }
    }
  }

  // -------------------------------------------------------------------------
  // STRICT MODE — refuse to leave any service on an undeclared feed. Move
  // them to `unplaced` so the operator sees a red warning rather than a
  // quiet "far" tag. Safest + Balanced run with strict=true; Aggressive
  // permissive (current behavior, undeclared placements allowed as last
  // resort).
  // -------------------------------------------------------------------------
  if (effectiveStrict) {
    for (const [pk, assignmentsMap] of partitionAssignments) {
      for (const assignment of [...assignmentsMap.values()]) {
        if (feedIsDeclared(assignment.feed)) continue;
        for (const svc of [...assignment.services]) {
          partitionUnplaced.get(pk)!.push(svc);
          const sizing = (svc[sizingField] as number) || 0;
          removeServiceFromState(assignment.feed.feed_id, pk, sizing);
        }
        assignment.services = [];
        assignment.sum_amps = 0;
      }
    }
  }

  // Materialize PartitionPlan[] sorted by total partition demand
  // descending so the heaviest partition renders first.
  const partitions: PartitionPlan[] = [];
  const partitionTotals = new Map<string, number>();
  for (const [pk, info] of partitionInfo) {
    partitionTotals.set(
      pk,
      info.services.reduce(
        (acc, s) => acc + ((s[sizingField] as number) || 0),
        0,
      ),
    );
  }
  const orderedKeys = [...partitionInfo.keys()].sort(
    (a, b) => (partitionTotals.get(b) ?? 0) - (partitionTotals.get(a) ?? 0),
  );
  for (const pk of orderedKeys) {
    const info = partitionInfo.get(pk)!;
    const assignmentsMap = partitionAssignments.get(pk)!;
    const assignments: FeedAssignment[] = [...assignmentsMap.values()]
      // Skip emptied assignments left behind by the relocator / strict
      // mode (services were moved away or unplaced).
      .filter((a) => a.services.length > 0)
      .map((a) => {
        // Cross-partition services on the same feed (not in THIS partition).
        // For each other partition that landed services on this feed, list
        // its services so the UI can render "shared with: <identity>".
        const partsOnFeed = feedPartitions.get(a.feed.feed_id) ?? new Set();
        const crossPartition: Array<{
          touring_identity: string | null;
          services: SuggestionsServiceInput[];
        }> = [];
        for (const otherPk of partsOnFeed) {
          if (otherPk === pk) continue;
          const otherAssign = partitionAssignments.get(otherPk)?.get(a.feed.feed_id);
          if (!otherAssign) continue;
          crossPartition.push({
            touring_identity: partitionInfo.get(otherPk)?.touring_identity ?? null,
            services: otherAssign.services,
          });
        }
        const feedSumTotal = globalFeedSums.get(a.feed.feed_id) ?? a.sum_amps;
        return {
          feed: a.feed,
          services: a.services,
          sum_amps: a.sum_amps,
          // Utilization is the GLOBAL combined load on this feed (across
          // every partition sharing it). Honors Safest/Balanced's
          // sum-of-show-max invariant — the gauge tells the truth about
          // the breaker, not just this partition's slice.
          utilization: feedSumTotal / a.feed.capacity_amps,
          proximity: a.proximity,
          affinity: a.affinity,
          feed_sum_amps_total: feedSumTotal,
          cross_partition_services: crossPartition,
        };
      })
      .sort((a, b) => {
        const po: Record<ProximityTier, number> = {
          assigned: 0, exact: 1, adjacent: 2, fallback: 3,
        };
        if (po[a.proximity] !== po[b.proximity]) {
          return po[a.proximity] - po[b.proximity];
        }
        // Same tier — rank by affinity (closer first), then utilization.
        if (a.affinity !== b.affinity) return b.affinity - a.affinity;
        return b.utilization - a.utilization;
      });
    partitions.push({
      tier: info.tier,
      touring_identity: info.touring_identity,
      assignments,
      unplaced: partitionUnplaced.get(pk) ?? [],
    });
  }
  const unused_feeds = activeFeeds.filter((f) => !feedOwner.has(f.feed_id));
  return { partitions, unused_feeds, strict_auto_disabled };
}
